import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, Gift } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';

const STATUS_LABEL = { active: 'Active', matured: 'Matured', redeemed: 'Redeemed', defaulted: 'Defaulted', closed: 'Closed' };
const STATUS_TONE = {
  active: 'border-muted text-muted',
  matured: 'border-primary text-primary',
  redeemed: 'border-success text-success',
  defaulted: 'border-danger text-danger',
  closed: 'border-danger text-danger',
};
const INST_STATUS_LABEL = { pending: 'Pending', paid: 'Paid', overdue: 'Overdue', waived: 'Waived' };
const INST_STATUS_TONE = { pending: 'text-muted', paid: 'text-success', overdue: 'text-danger', waived: 'text-warning' };

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function AccountViewPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [payMonth, setPayMonth] = useState(null);
  const [payForm, setPayForm] = useState(null);
  const [payError, setPayError] = useState(null);

  const [cancelMonth, setCancelMonth] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState(null);

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closeError, setCloseError] = useState(null);

  const [redeemOpen, setRedeemOpen] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemError, setRedeemError] = useState(null);

  const [pdfError, setPdfError] = useState(null);

  const { data: account, isLoading } = useQuery({
    queryKey: ['chit', 'accounts', id],
    queryFn: () => api.get(`/chit/accounts/${id}`),
  });

  const ratesQuery = useQuery({ queryKey: ['rates', 'today'], queryFn: () => api.get('/rates/today') });

  const debouncedInvoiceSearch = useDebouncedValue(invoiceSearch, 400);
  const invoiceLookupQuery = useQuery({
    queryKey: ['invoices', 'search', debouncedInvoiceSearch],
    queryFn: () => api.get('/invoices', { query: { search: debouncedInvoiceSearch, pageSize: 5 } }),
    enabled: redeemOpen && debouncedInvoiceSearch.length >= 3 && !selectedInvoice,
  });
  const invoiceMatches = invoiceLookupQuery.data?.data ?? [];

  const payMutation = useMutation({
    mutationFn: () =>
      api.post(`/chit/accounts/${id}/installments/${payMonth}/pay`, {
        amountPaid: Number(payForm.amountPaid),
        paymentDate: payForm.paymentDate,
        paymentMode: payForm.paymentMode,
        referenceNo: payForm.referenceNo || undefined,
        ratePerGram: payForm.ratePerGram ? Number(payForm.ratePerGram) : undefined,
        notes: payForm.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts', id] });
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts'] });
      setPayMonth(null);
    },
    onError: (err) => setPayError(err instanceof ApiClientError ? err.message : 'Could not record this payment.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/chit/accounts/${id}/installments/${cancelMonth}/cancel`, { reason: cancelReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts', id] });
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts'] });
      setCancelMonth(null);
      setCancelReason('');
    },
    onError: (err) => setCancelError(err instanceof ApiClientError ? err.message : 'Could not cancel this payment.'),
  });

  const closeMutation = useMutation({
    mutationFn: () => api.post(`/chit/accounts/${id}/close`, { reason: closeReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts', id] });
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts'] });
      setCloseOpen(false);
    },
    onError: (err) => setCloseError(err instanceof ApiClientError ? err.message : 'Could not close this account.'),
  });

  const redeemMutation = useMutation({
    mutationFn: () => api.post(`/chit/accounts/${id}/redeem`, { invoiceId: selectedInvoice.id, amount: Number(redeemAmount) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts', id] });
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts'] });
      setRedeemOpen(false);
    },
    onError: (err) => setRedeemError(err instanceof ApiClientError ? err.message : 'Could not redeem this account.'),
  });

  if (isLoading || !account) return null;

  const isGold = account.scheme_type === 'gold';
  const purityLabel = account.purity_metal ? `${account.purity_metal} ${account.purity_label}` : null;
  const todaysRate = isGold ? ratesQuery.data?.purities?.find((p) => p.purityId === account.scheme_purity_id)?.ratePerGram : null;
  const maturityValue = isGold
    ? todaysRate
      ? Number(account.total_grams) * Number(todaysRate)
      : null
    : Number(account.total_paid) + Number(account.bonus_amount);

  const openPay = (installment) => {
    setPayError(null);
    setPayMonth(installment.month_number);
    setPayForm({
      amountPaid: installment.amount_due,
      paymentDate: today(),
      paymentMode: 'cash',
      referenceNo: '',
      ratePerGram: isGold ? (todaysRate ?? '') : '',
      notes: '',
    });
  };

  const openRedeem = () => {
    setRedeemError(null);
    setSelectedInvoice(null);
    setInvoiceSearch('');
    setRedeemAmount(maturityValue ? String(Math.round(maturityValue * 100) / 100) : '');
    setRedeemOpen(true);
  };

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/chit" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{account.scheme_no}</h1>
          <p className="text-sm text-muted">{account.scheme_name} · {isGold ? 'Gold accumulation' : 'Rupee-based'}</p>
        </div>
        <span className={`ml-auto rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[account.status]}`}>
          {STATUS_LABEL[account.status]}
        </span>
      </div>

      {account.status === 'closed' ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          Closed {new Date(account.closed_at).toLocaleDateString('en-IN')}: {account.closed_reason}
        </p>
      ) : null}

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Customer</h2>
          <p className="font-medium">{account.customer_name}</p>
          {account.customer_mobile ? <p className="text-sm text-muted">{account.customer_mobile}</p> : null}
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Plan</h2>
          <p className="font-medium">{formatINR(account.monthly_amount)} / month · {account.duration_months} months</p>
          <p className="text-sm text-muted">
            Joined {new Date(account.joining_date).toLocaleDateString('en-IN')} · Matures {new Date(account.maturity_date).toLocaleDateString('en-IN')}
          </p>
          {isGold && purityLabel ? <p className="text-sm text-muted">Purity: {purityLabel}</p> : null}
        </div>
      </section>

      {pdfError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {pdfError}
        </p>
      ) : null}

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Installments</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Due</th>
                <th className="py-2 pr-3 text-right">Amount due</th>
                <th className="py-2 pr-3 text-right">Paid</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Receipt</th>
                <th className="py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {account.installments.map((inst) => (
                <tr key={inst.id} className="border-b border-border last:border-0">
                  <td className="tabular py-2 pr-3">{inst.month_number}</td>
                  <td className="py-2 pr-3">{new Date(inst.due_date).toLocaleDateString('en-IN')}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatINR(inst.amount_due)}</td>
                  <td className="tabular py-2 pr-3 text-right">{inst.status === 'paid' ? formatINR(inst.amount_paid) : '—'}</td>
                  <td className={`py-2 pr-3 ${INST_STATUS_TONE[inst.status]}`}>{INST_STATUS_LABEL[inst.status]}</td>
                  <td className="py-2 pr-3">{inst.receipt_no ?? '—'}</td>
                  <td className="py-2 text-right">
                    {inst.status === 'paid' ? (
                      <div className="flex justify-end gap-2">
                        <PdfActions
                          compact
                          path={`/chit/accounts/${id}/installments/${inst.month_number}/pdf`}
                          documentLabel={
                            inst.receipt_no ? `Chit receipt ${inst.receipt_no}` : `Chit receipt — month ${inst.month_number}`
                          }
                          fileBaseName={inst.receipt_no ?? `chit-${id}-${inst.month_number}`}
                          onError={setPdfError}
                        />
                        <ThermalPrintButton printPath={`/chit/accounts/${id}/installments/${inst.month_number}`} compact />
                        {account.status === 'active' || account.status === 'matured' ? (
                          <button
                            type="button"
                            onClick={() => { setCancelError(null); setCancelMonth(inst.month_number); setCancelReason(''); }}
                            className="text-muted hover:text-danger"
                            aria-label="Cancel payment"
                          >
                            <Ban size={16} aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    ) : account.status === 'active' ? (
                      <Button size="sm" variant="secondary" onClick={() => openPay(inst)}>Pay</Button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card ml-auto w-full max-w-sm p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted">Installments paid</span>
          <span className="tabular font-semibold">{account.installments_paid} / {account.duration_months}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total paid</span>
          <span className="tabular font-semibold">{formatINR(account.total_paid)}</span>
        </div>
        {isGold ? (
          <div className="flex justify-between text-sm">
            <span className="text-muted">Total grams accrued</span>
            <span className="tabular font-semibold">{formatWeight(account.total_grams)}</span>
          </div>
        ) : Number(account.bonus_amount) > 0 ? (
          <div className="flex justify-between text-sm text-success">
            <span>Maturity bonus</span>
            <span className="tabular font-semibold">{formatINR(account.bonus_amount)}</span>
          </div>
        ) : null}
        {account.status === 'redeemed' ? (
          <div className="flex justify-between text-sm text-success">
            <span>Redeemed</span>
            <span className="tabular font-semibold">{new Date(account.redeemed_at).toLocaleDateString('en-IN')}</span>
          </div>
        ) : null}
      </section>

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        {account.status === 'active' ? (
          <Button variant="danger" onClick={() => { setCloseError(null); setCloseOpen(true); }} className="flex-1 sm:flex-none">
            <Ban size={17} aria-hidden="true" /> Close early
          </Button>
        ) : null}
        {account.status === 'matured' ? (
          <Button onClick={openRedeem} className="flex-1 sm:flex-none">
            <Gift size={17} aria-hidden="true" /> Redeem against a sale
          </Button>
        ) : null}
      </div>

      {/* Pay installment ------------------------------------------------- */}
      <Modal open={!!payMonth} onClose={() => setPayMonth(null)} title={`Record payment — installment ${payMonth}`}>
        {payForm ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField label="Amount paid (₹)" type="number" inputMode="decimal" required value={payForm.amountPaid} onChange={(e) => setPayForm((f) => ({ ...f, amountPaid: e.target.value }))} />
              <TextField label="Payment date" type="date" required value={payForm.paymentDate} onChange={(e) => setPayForm((f) => ({ ...f, paymentDate: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField label="Mode" value={payForm.paymentMode} onChange={(e) => setPayForm((f) => ({ ...f, paymentMode: e.target.value }))}>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="netbanking">Net banking</option>
                <option value="cheque">Cheque</option>
              </SelectField>
              <TextField label="Reference number" value={payForm.referenceNo} onChange={(e) => setPayForm((f) => ({ ...f, referenceNo: e.target.value }))} />
            </div>
            {isGold ? (
              <TextField
                label="Rate today (₹/g)"
                type="number"
                inputMode="decimal"
                required
                value={payForm.ratePerGram}
                onChange={(e) => setPayForm((f) => ({ ...f, ratePerGram: e.target.value }))}
                hint={todaysRate ? undefined : "No rate on file for this purity today — enter it manually"}
              />
            ) : null}
            <TextField label="Notes" value={payForm.notes} onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))} />
            {payError ? <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{payError}</p> : null}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setPayMonth(null)}>Cancel</Button>
              <Button
                disabled={!payForm.amountPaid || Number(payForm.amountPaid) <= 0 || (isGold && !payForm.ratePerGram)}
                loading={payMutation.isPending}
                onClick={() => payMutation.mutate()}
              >
                <CheckCircle2 size={16} aria-hidden="true" /> Record payment
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Cancel a payment -------------------------------------------------- */}
      <Modal open={!!cancelMonth} onClose={() => setCancelMonth(null)} title="Cancel this payment?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Installment {cancelMonth} goes back to pending and its receipt number is released. The account's totals
            are recalculated.
          </p>
          <TextAreaField label="Reason" required value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          {cancelError ? <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{cancelError}</p> : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelMonth(null)}>Keep payment</Button>
            <Button variant="danger" disabled={cancelReason.trim().length < 3} loading={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>

      {/* Close account early ------------------------------------------------ */}
      <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Close this account?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The scheme number <b>{account.scheme_no}</b> stays reserved. Any unpaid installments simply stop being
            collected — this can't be undone from here.
          </p>
          <TextAreaField label="Reason" required value={closeReason} onChange={(e) => setCloseReason(e.target.value)} />
          {closeError ? <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{closeError}</p> : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCloseOpen(false)}>Keep account</Button>
            <Button variant="danger" disabled={closeReason.trim().length < 3} loading={closeMutation.isPending} onClick={() => closeMutation.mutate()}>
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm closure
            </Button>
          </div>
        </div>
      </Modal>

      {/* Redeem against a sale -------------------------------------------- */}
      <Modal open={redeemOpen} onClose={() => setRedeemOpen(false)} title="Redeem against a sale invoice">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Normally this happens straight from the invoice — matured chit accounts turn up there for the customer
            being billed, and issuing the invoice redeems and closes this account automatically. Use this only for
            a sale that's already been issued without linking this account: find that invoice below to mark this
            account redeemed against it.
          </p>
          <div className="relative">
            {selectedInvoice ? (
              <div className="flex items-center justify-between rounded-card border border-border bg-surface-2 px-3.5 py-2.5 text-sm">
                <span>Redeeming against <b>{selectedInvoice.invoice_no}</b></span>
                <button type="button" onClick={() => setSelectedInvoice(null)} className="text-primary hover:underline">Change</button>
              </div>
            ) : (
              <>
                <TextField label="Find the invoice" value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} placeholder="Invoice number, customer or mobile…" />
                {invoiceMatches.length > 0 ? (
                  <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                    {invoiceMatches.map((inv) => (
                      <button key={inv.id} type="button" onClick={() => setSelectedInvoice(inv)} className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm hover:bg-surface-2">
                        <span className="font-medium">{inv.invoice_no}</span>
                        <span className="text-muted">{inv.customer_name} · {formatINR(inv.amount_payable)}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
          <TextField
            label="Redemption amount (₹)"
            type="number"
            inputMode="decimal"
            required
            value={redeemAmount}
            onChange={(e) => setRedeemAmount(e.target.value)}
            hint={isGold ? (todaysRate ? `Suggested from today's rate: ${formatINR(maturityValue)}` : 'No rate on file today — enter the agreed value') : `Paid + bonus: ${formatINR(maturityValue)}`}
          />
          {redeemError ? <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{redeemError}</p> : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRedeemOpen(false)}>Cancel</Button>
            <Button disabled={!selectedInvoice || !redeemAmount || Number(redeemAmount) <= 0} loading={redeemMutation.isPending} onClick={() => redeemMutation.mutate()}>
              <Gift size={16} aria-hidden="true" /> Confirm redemption
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
